import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api, STATUS_LABELS, formatEuro } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import OpportunityCard from "../components/OpportunityCard";
import OpportunityDialog from "../components/OpportunityDialog";
import { Plus } from "lucide-react";

const AGENT_COLS = ["interesado", "posible_cierre", "ganado", "perdido"];
const ADMIN_COLS = ["interesado", "posible_cierre", "ganado", "perdido"];

const ColumnHeader = ({ status, count, total, showTotal }) => {
    const accent = {
        interesado: "bg-[#002FA7]",
        posible_cierre: "bg-[#FF9900]",
        ganado: "bg-[#008F11]",
        perdido: "bg-[#D32F2F]",
    }[status];
    return (
        <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
                <span className={`w-2 h-2 ${accent}`} />
                <h3 className="font-display text-sm uppercase tracking-wider">
                    {STATUS_LABELS[status]}
                </h3>
                <span
                    className="text-xs text-neutral-500"
                    data-testid={`col-count-${status}`}
                >
                    ({count})
                </span>
            </div>
            {showTotal && (
                <span className="text-xs font-medium" data-testid={`col-total-${status}`}>
                    {formatEuro(total)}
                </span>
            )}
        </div>
    );
};

export default function Board() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const columns = isAdmin ? ADMIN_COLS : AGENT_COLS;

    const [ops, setOps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const fetchOps = useCallback(async () => {
        try {
            const r = await api.get("/opportunities");
            setOps(r.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOps();
    }, [fetchOps]);

    const grouped = useMemo(() => {
        const g = { interesado: [], posible_cierre: [], ganado: [], perdido: [] };
        ops.forEach((o) => {
            if (g[o.status]) g[o.status].push(o);
        });
        return g;
    }, [ops]);

    const openCreate = () => {
        setEditing(null);
        setDialogOpen(true);
    };
    const openEdit = (op) => {
        setEditing(op);
        setDialogOpen(true);
    };

    return (
        <div className="px-6 lg:px-10 py-8" data-testid="board-page">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                <div>
                    <span className="label-eyebrow text-neutral-500">Tablero</span>
                    <h1 className="font-display text-4xl lg:text-5xl tracking-tight mt-1">
                        Oportunidades
                    </h1>
                    <p className="mt-2 text-sm text-neutral-600 max-w-lg">
                        {isAdmin
                            ? "Vista completa del pipeline. Tú decides qué se gana y qué se pierde."
                            : "Tu pipeline personal. Sube tus oportunidades y muévelas según avanzan."}
                    </p>
                </div>
                <button
                    onClick={openCreate}
                    data-testid="create-opportunity-btn"
                    className="brutal-btn px-6 py-3 text-sm flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    Nueva oportunidad
                </button>
            </div>

            {loading ? (
                <div className="text-sm text-neutral-500">Cargando…</div>
            ) : (
                <div
                    className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
                >
                    {columns.map((status) => {
                        const list = grouped[status];
                        const total = list.reduce((s, o) => s + (o.amount || 0), 0);
                        return (
                            <section
                                key={status}
                                className="kanban-col"
                                data-testid={`col-${status}`}
                            >
                                <ColumnHeader status={status} count={list.length} total={total} showTotal={isAdmin} />
                                <div className="flex-1 flex flex-col gap-3">
                                    {list.length === 0 && (
                                        <div className="text-xs text-neutral-400 border border-dashed border-black/15 p-6 text-center">
                                            Sin oportunidades.
                                        </div>
                                    )}
                                    {list.map((op) => (
                                        <OpportunityCard
                                            key={op.id}
                                            op={op}
                                            isAdmin={isAdmin}
                                            onChanged={fetchOps}
                                            onEdit={openEdit}
                                        />
                                    ))}
                                </div>
                            </section>
                        );
                    })}
                </div>
            )}

            <OpportunityDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                onSaved={fetchOps}
                initial={editing}
            />
        </div>
    );
}
